import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type Job } from '@/features/jobs/Job'
import { type BookkeepingProgressionStore } from '@/features/jobs/bookkeeping/BookkeepingProgressionStore'
import {
  type AvailableBookkeepingSoftwareType,
  getAvailableBookkeepingSoftwareToken,
} from '@/features/jobs/bookkeeping/platform-connections/available-bookkeeping-software'
import type { BookkeepingPlatform } from '@/features/jobs/bookkeeping/platform-connections/bookkeeping-platform'
import type { BookkeepingSoftware } from '@/features/jobs/bookkeeping/bookkeeping-software'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { extractMessageFromError } from '@/utils/error-util'

/**
 * The page of the grant access dialog being shown.
 */
export type BookkeepingGrantAccessDialogPage =
  | { type: 'SelectSoftware' }
  | { type: 'Instructions'; platform: BookkeepingPlatform }

/**
 * The software selected by default when the client picks the `Other` platform.
 */
const DEFAULT_OTHER_SOFTWARE: AvailableBookkeepingSoftwareType = 'Freshbooks'

/**
 * Walks the client, or an admin for them, through granting the Pro access to their books.
 */
export class BookkeepingGrantAccessDialogViewModel {
  readonly modal = new ModalViewModel()

  /**
   * The page being shown.
   */
  @observable accessor page: BookkeepingGrantAccessDialogPage = {
    type: 'SelectSoftware',
  }

  /**
   * The software the client selected when their platform is `Other`.
   */
  @observable accessor otherSoftware: AvailableBookkeepingSoftwareType =
    DEFAULT_OTHER_SOFTWARE

  constructor(
    private readonly _job: Job,
    private readonly _isClientCollaborator: () => boolean,
    private readonly _bookkeepingProgressionStore: BookkeepingProgressionStore,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * The Pro the client needs to grant access to.
   */
  @computed
  get pro() {
    return this._job.championProvider?.member
  }

  /**
   * Whether the Pro's details needed to grant access are available.
   */
  @computed
  get hasProDetails() {
    return Boolean(this.pro?.email)
  }

  /**
   * Whether the current user is the client, as opposed to an admin granting
   * access on their behalf.
   */
  @computed
  get isClientPerspective() {
    return this._isClientCollaborator()
  }

  /**
   * The software the client selected, if they have selected one.
   */
  @computed
  get software(): BookkeepingSoftware | null {
    if (this.page.type !== 'Instructions') {
      return null
    }

    switch (this.page.platform) {
      case 'QuickBooks':
        return { type: 'QuickBooks' }
      case 'Xero':
        return { type: 'Xero' }
      case 'Other':
        return {
          type: 'Other',
          name: getAvailableBookkeepingSoftwareToken(this.otherSoftware),
        }
    }
  }

  /**
   * Shows the dialog, starting over from the software selection.
   */
  @action.bound
  show() {
    this.page = { type: 'SelectSoftware' }
    this.otherSoftware = DEFAULT_OTHER_SOFTWARE
    this.confirmAccessProvided.reset()
    this.modal.show()
  }

  /**
   * Selects the platform and shows its instructions.
   */
  @action.bound
  selectPlatform(platform: BookkeepingPlatform) {
    this.page = { type: 'Instructions', platform }
  }

  /**
   * Goes back to the software selection.
   */
  @action.bound
  goBack() {
    this.page = { type: 'SelectSoftware' }
  }

  /**
   * Copies a given value to the clipboard.
   */
  @action.bound
  async copyToClipboard(value: string) {
    try {
      await navigator.clipboard.writeText(value)
    } catch (err: unknown) {
      this._newAlert()
        .withState('error')
        .saying('Copying to the clipboard failed', extractMessageFromError(err))
        .autoDismiss(20_000)
        .show()
      return
    }

    this._newAlert()
      .withState('success')
      .saying('Copied!', `${value} was copied to the clipboard.`)
      .autoDismiss()
      .show()
  }

  /**
   * Confirms the client granted their Pro access to their books.
   */
  confirmAccessProvided = task.resolved(async () => {
    const software = this.software

    if (!software || (software.type === 'Other' && !software.name.trim())) {
      this._newAlert()
        .withState('warning')
        .saying(
          'Bookkeeping software is required',
          'Please select the software used for the books before confirming access.',
        )
        .autoDismiss()
        .show()
      return
    }

    await this._bookkeepingProgressionStore
      .confirmUnmanagedClientAccess(this._job.id, software)
      .catch(this._createAlertErrorHandler('Confirming access failed'))

    this.modal.close()
  })
}
